import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { LoadingState, WaveBackgroundLayout } from "~/common/components";
import { INVITATION_CODE } from "~/common/constants";
import { NewPatientSignUpRenderer } from "../components/signUp/signUpNewPatient/NewPatientSignUpRenderer";
import { useGetInvitationLink } from "../hooks";
import { useValidateSignupInfo } from "../hooks/signUp";
import { useSignUpNewPatient } from "../hooks/signUp/useSignUpNewPatient";
import { ROUTES } from "../router";
import { useOnBoardingNewPatientStore } from "../stores/useOnBoardingNewPatientStore";
import {
  PATIENTS_TYPES,
  signUpPatientSchema,
  SignUpPatientSchemaType,
} from "../utils";

export const SignUpNewPatient = () => {
  const { newPatient } = useOnBoardingNewPatientStore();
  const [providerId, setProviderId] = useState<string>();
  const [organizationId, setOrganizationId] = useState<string>();
  const [searchParams] = useSearchParams();
  const invitationCodeParam = searchParams.get(INVITATION_CODE);
  const navigate = useNavigate();

  const [isFetchingValidation, setIsFetchingValidation] = useState<boolean>();

  const { data: invitationCodeData } = useGetInvitationLink(
    invitationCodeParam ?? "",
  );

  useEffect(() => {
    if (invitationCodeData?.is_active === false) {
      navigate(ROUTES.consultationInactive);
    }
  }, [invitationCodeData, navigate]);

  const {
    formState: { errors },
    register,
    control,
    getValues,
    setValue,
    watch,
    trigger,
    setError,
    clearErrors,
  } = useForm<SignUpPatientSchemaType>({
    defaultValues: {
      ...newPatient,
      type: PATIENTS_TYPES.newPatient,
    },
    resolver: zodResolver(signUpPatientSchema),
    mode: "all",
  });

  const { validateSignUpNewPatientToken, invitationCode } =
    useValidateSignupInfo();

  const {
    onNext,
    onRetry,
    canResendAt,
    onBack,
    isActionPending,
    onSubmitSignUp,
  } = useSignUpNewPatient({
    getValues,
    trigger,
    setError,
    setValue,
    clearErrors,
    providerId,
    organizationId,
    invitationCode: invitationCode ?? undefined,
  });

  useEffect(() => {
    const { isFetching, providerId, organizationId } =
      validateSignUpNewPatientToken();

    setIsFetchingValidation(isFetching);
    setProviderId(providerId);
    setOrganizationId(organizationId);
  }, [validateSignUpNewPatientToken, navigate]);

  if (!organizationId || isFetchingValidation)
    return <LoadingState className="h-screen" />;

  return (
    <WaveBackgroundLayout>
      <NewPatientSignUpRenderer
        onNext={onNext}
        onRetry={onRetry}
        canResendAt={canResendAt}
        handleRegistration={onSubmitSignUp}
        handleGoBack={onBack}
        register={register}
        errors={errors}
        setValue={setValue}
        control={control}
        watch={watch}
        isPending={isActionPending}
        setError={setError}
      />
    </WaveBackgroundLayout>
  );
};
